import {
  ClientEntity,
  OperationalEntity
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {CreatePlanTenantWarning} from "../view-model";

const extractTenantIds = (entities?: OperationalEntity[] | null): string[] =>
  (entities || [])
    .map(oe => oe.tenantId)
    .filter((id): id is string => id != null && id !== "");

const collectAllTenantIds = (entity: ClientEntity): string[] => {
  const tenantIds = [...extractTenantIds(entity.mappedOperationalEntities)];

  entity.children?.forEach(child => {
    tenantIds.push(...collectAllTenantIds(child));
  });

  return tenantIds;
};

export interface TenantResolution {
  tenantId?: string;
  warning?: CreatePlanTenantWarning;
}

/**
 * Validates tenant data integrity across the entire client hierarchy and extracts the tenant ID
 * from the ultimate parent's mapped operational entities.
 *
 * Validation: Ensures the hierarchy can resolve to exactly one ultimate-parent tenant,
 * even if descendants expose conflicting tenant IDs that should be surfaced as a warning.
 * Extraction: Returns the tenant ID from the ultimate parent only.
 *
 * Handles five distinct flows:
 * 1. "Known Tenant": The client entity has an ultimate parent with one or more operational entities within a single tenant → Returns that tenant ID.
 * 2. "Warning + Known Tenant": The client hierarchy contains multiple tenant IDs, but the ultimate parent resolves to exactly one tenant → Returns the ultimate parent tenant plus warning metadata.
 * 3. "New Tenant Creation": The client entity is the ultimate parent (no ancestors) with no mapped operational entities → Returns undefined (tenant needs creation).
 * 4. "No Tenancy Established" Error: The client has ancestors, but the ultimate parent either has no mapped OEs or all mapped OEs lack tenancy → Throws error.
 * 5. "Multiple Tenants" Error: Different tenant IDs exist across the hierarchy and the ultimate parent does not resolve to exactly one tenant → Throws error.
 * 6. "Data Integrity" Error: The client is the ultimate parent with mapped OEs but none have tenancy → Throws error.
 *
 * @param source - The client entity with hierarchy
 * @returns The tenant resolution with tenant ID and optional warning metadata
 * @throws Error if tenancy cannot be established or multiple tenants detected across the hierarchy
 */
export const validateAndExtractTenantId = (
  source: ClientEntity
): TenantResolution => {
  // source.hierarchy refers to the ultimate parent, or we can default to the source itself if there is no hierarchy attribute.
  const ultimateParent = source.hierarchy || source;

  // Check if the source entity has ancestors by looking at custParentId or custUltimateParentId
  const hasAncestors = !!(source.custParentId || source.custUltimateParentId);

  // Collect all tenant IDs across the entire hierarchy
  const uniqueTenantIds = Array.from(new Set(collectAllTenantIds(ultimateParent)));

  // Extract tenant IDs from the ultimate parent only (for return value)
  const mappedEntities = ultimateParent.mappedOperationalEntities;
  const uniqueUltimateParentTenantIds = Array.from(
    new Set(extractTenantIds(mappedEntities))
  );

  // Case 2 / 4: Multi-tenant hierarchy warning or error
  if (uniqueTenantIds.length > 1) {
    if (uniqueUltimateParentTenantIds.length === 1) {
      const tenantId = uniqueUltimateParentTenantIds[0];

      return {
        tenantId,
        warning: {
          type: "multiple-tenants",
          tenantIdUsedForCreation: tenantId,
          tenantIds: uniqueTenantIds
        }
      };
    }

    throw new Error(
      `This client is associated with multiple tenants, and we cannot determine the correct tenant to use for new entities. Please contact COAT support. Associated tenants: ${uniqueTenantIds.join(", ")}`
    );
  }

  if (uniqueUltimateParentTenantIds.length > 0) {
    // Case 1: "Known Tenant" - Ultimate parent has OE(s) within a single tenant
    return {tenantId: uniqueUltimateParentTenantIds[0]};
  }

  // Case 3: "New Tenant Creation" - Entity is the ultimate parent with no mapped OEs
  if (!hasAncestors) {
    // If the ultimate parent has mapped OEs but none have tenancy, this is a data integrity error
    if (mappedEntities && mappedEntities.length > 0) {
      throw new Error(
        `Data integrity error for ${source.caId}. The ultimate parent has mapped operational entities, but none have tenancy established.`
      );
    }
    // No mapped OEs - downstream logic will create the tenant when this function returns { tenantId: undefined }.
    return {tenantId: undefined};
  }

  // Case 4: "No Tenancy Established" Error - Entity has ancestors, but they have no tenancy.
  if (!mappedEntities || mappedEntities.length === 0) {
    throw new Error(
      `Cannot create or assign a tenant for ${source.caId}. The ultimate parent has no mapped operational entities. Tenancy must be established on the ultimate parent.`
    );
  } else {
    throw new Error(
      `Cannot create or assign a tenant for ${source.caId}. The ultimate parent has mapped operational entities, but none have tenancy established. Tenancy must be established on the ultimate parent.`
    );
  }
};
